import { motion } from "framer-motion"
import { Phone, Send, Facebook, Instagram, Youtube } from "lucide-react"

interface FooterProps {
  t: Record<string, any>
}

export function Footer({ t }: FooterProps) {
  const contactInfo = [
    {
      icon: Phone,
      label: "Telefon",
      value: "+998 97 377 33 66",
      href: "tel:+998973773366",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: Phone,
      label: "Qo'shimcha",
      value: "93 612 73 33",
      href: "tel:+998936127333",
      gradient: "from-cyan-500 to-blue-500",
    },
    {
      icon: Phone,
      label: "Qo'shimcha",
      value: "79 572 33 66",
      href: "tel:+998795723366",
      gradient: "from-blue-500 to-purple-500",
    },
    {
      icon: Send,
      label: "Telegram",
      value: "@cardintaxi",
      href: "https://t.me/cardintaxi",
      gradient: "from-cyan-500 to-blue-500",
    },
  ]

  const quickLinks = [
    { label: t?.nav?.features || "Xususiyatlar", href: "#features" },
    { label: t?.nav?.how || "Qanday ishlaydi", href: "#how" },
    { label: t?.nav?.about || "Biz haqimizda", href: "#about" },
    { label: t?.nav?.contact || "Aloqa", href: "#contact" },
  ]

  const socialLinks = [
    { icon: Facebook, href: "https://facebook.com/cardintaxi", label: "Facebook" },
    { icon: Instagram, href: "https://instagram.com/cardin_uz?igshid=OGQ5ZDc2ODk2ZA==", label: "Instagram" },
    { icon: Send, href: "https://t.me/cardintaxi", label: "Telegram" },
    { icon: Youtube, href: "https://www.youtube.com/shorts/PWjvHt1zADM", label: "Youtube" },
  ]

  return (
    <footer className="relative bg-gradient-to-b from-black via-gray-950 to-black border-t border-white/10 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-12">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            {/* Logo */}
            <div className="flex items-baseline gap-2 mb-6">
              <motion.span
                className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent"
                style={{ backgroundSize: "200% auto" }}
                animate={{
                  backgroundPosition: ["0% center", "200% center", "0% center"],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                Cardin
              </motion.span>
              <motion.span
                className="text-3xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 bg-clip-text text-transparent"
                style={{ backgroundSize: "200% auto" }}
                animate={{
                  backgroundPosition: ["200% center", "0% center", "200% center"],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                Taxi
              </motion.span>
            </div>

            <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
              Shahar bo'ylab tez, ishonchli va qulay taksi xizmati. Bizning ilovamiz bilan har safar - xavfsiz safar.
            </p>

            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social, idx) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.3 }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/50 flex items-center justify-center transition-all duration-300 group"
                >
                  <social.icon className="h-5 w-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h3 className="text-xl font-bold text-white mb-6">Tezkor havolalar</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, idx) => (
                <motion.li
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-blue-400 transition-all duration-300" />
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-1"
          >
            <h3 className="text-xl font-bold text-white mb-6">Bog'lanish</h3>
            <div className="space-y-4">
              {contactInfo.map((contact, idx) => (
                <motion.a
                  key={contact.label}
                  href={contact.href}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-3 group"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${contact.gradient} p-2.5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <contact.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-0.5">{contact.label}</p>
                    <p className="text-white font-medium group-hover:text-blue-400 transition-colors">
                      {contact.value}
                    </p>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="border-t border-white/10 pt-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            {/* Copyright */}
            <div className="text-gray-400 text-center md:text-left">
              <p>© 2025 . Barcha huquqlar himoyalangan.</p>
              <p className="text-xs text-gray-500 mt-1">
                Asosiy sayt:{" "}
                <a
                  href="https://cardin.uz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  cardin.uz
                </a>
              </p>
            </div>

            {/* Developers */}
            <div className="flex items-center gap-2 text-gray-400">
              <span>Ishlab chiqilgan:</span>
              <div className="flex items-center gap-3">
                <a
                  href="https://myweb.uz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent hover:from-blue-300 hover:to-cyan-300 transition-all duration-300"
                >
                  Myweb.Uz
                </a>
                <span className="text-gray-600">&</span>
                <a
                  href="https://ibots.uz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent hover:from-cyan-300 hover:to-blue-300 transition-all duration-300"
                >
                  iBots.Uz
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  )
}